import {
  Component,
  OnInit,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { MatChipsModule } from '@angular/material/chips';
import { Customer } from '../customer-table/customer-table.component';
import { CustomerTabViewComponent } from '../customer-tab-view/customer-tab-view.component';
import { CustomerStatusActiveModalComponent } from '../customer-status-active-modal/customer-status-active-modal.component';
import { CustomerStatusInactiveModalComponent } from '../customer-status-inactive-modal/customer-status-inactive-modal.component';
import { CustomerService } from '../../services/customer.service';
import { VendorModeService } from '../../services/vendor-mode.service';

/**
 * Modal component to view and manage a single customer's details, including toggling active/inactive status with confirmation dialogs and emitting updates to parent components.
 * @component
 * @selector app-customer-details-modal
 * @example
 *   <app-customer-details-modal
 *     [customer]="selectedCustomer"
 *     [isVisible]="isModalVisible"
 *     (close)="handleClose()"
 *     (statusUpdated)="handleStatusUpdated()"
 *     (customerUpdated)="handleCustomerUpdated()">
 *   </app-customer-details-modal>
 * @input {Customer | null} customer - The customer object to display and manage in the modal. When null, the modal shows no customer data.
 * @input {boolean} isVisible - Controls whether the modal is shown.
 * @output {EventEmitter<void>} close - Emitted when the modal should be closed (explicit close action or backdrop click).
 * @output {EventEmitter<void>} statusUpdated - Emitted after the customer's status has been successfully changed on the server.
 * @output {EventEmitter<void>} customerUpdated - Emitted when the customer record has been updated by child components or actions within the modal.
 *
 * Key methods:
 * - onToggleChange(event): Intercepts status toggle attempts, prevents immediate toggle, sets pendingStatus and opens the appropriate confirmation modal.
 * - onActiveModalConfirm()/onInactiveModalConfirm(): Confirm handlers that call updateCustomerStatus to persist status changes.
 * - updateCustomerStatus(status): Calls CustomerService to update the customer's status, handles success and error flows, shows toasts, updates local state, and emits statusUpdated.
 * - showErrorToast(message): Helper to display error messages via NotificationService.
 * - onClose(): Emits the close event to the parent.
 * - onBackdropClick(event): Closes the modal if the backdrop (outside content) is clicked.
 * - getInitials(name): Utility to compute initials from a name string.
 * - onCustomerUpdated(): Emits customerUpdated to inform parent components of internal updates.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Initializes toggle state based on the provided customer.
 * - ngOnChanges(): Re-evaluates toggle state when input properties (like customer) change.
 */
@Component({
  selector: 'app-customer-details-modal',
  standalone: true,
  imports: [
    CommonModule,
    MatChipsModule,
    CustomerTabViewComponent,
    CustomerStatusActiveModalComponent,
    CustomerStatusInactiveModalComponent,
  ],
  templateUrl: './customer-details-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./customer-details-modal.component.scss'],
})
export class CustomerDetailsModalComponent implements OnInit, OnChanges {
  @Input() customer: Customer | null = null;
  @Input() isVisible = false;
  @Output() close = new EventEmitter<void>();
  @Output() statusUpdated = new EventEmitter<void>();
  @Output() customerUpdated = new EventEmitter<void>();
  /**
   * CWS-1881: a prepaid redemption row asked to open another customer. The modal shows
   * whichever customer it is given, so it hands the id up to the table that owns it.
   */
  @Output() openCustomer = new EventEmitter<string>();

  isCustomerActive = false;
  isUpdatingStatus = false;
  showActiveModal = false;
  showInactiveModal = false;
  pendingStatus = '';

  constructor(
    private customerService: CustomerService,
    private messageService: NotificationService,
    private vendorModeService: VendorModeService
  ) {}

  ngOnInit(): void {
    this.updateToggleState();
  }

  /**
   * Whether to show the vendor tag (e.g. "DRB") beside the customer's name.
   *
   * False for a Way-created customer, and false when that vendor's sync is turned
   * off for this shop — disabling vendor mode hides the framing without altering
   * the customer, their membership, or their gate access.
   *
   * @param source `customers.source`, e.g. 'drb'; null for an organic Way customer
   */
  showsVendorTag(source: string | null | undefined): boolean {
    return this.vendorModeService.isVendorSourceEnabled(source);
  }

  ngOnChanges(): void {
    this.updateToggleState();
  }

  private updateToggleState(): void {
    if (this.customer) {
      this.isCustomerActive = this.customer.customer_status === 'active';
    }
  }

  /**
   * Handle toggle click for the customer active checkbox: prevent immediate change and open the appropriate confirmation modal.
   * @example
   * nToggleChange({ target: { checked: false } })
   * void
   * @param {{any}} {{event}} - Event object from the checkbox change; used to revert the visual change and determine which confirmation modal to show.
   * @returns {{void}} Void; action is performed by updating component state and showing a modal.
   **/
  onToggleChange(event: any): void {
    // Prevent the checkbox change since we need confirmation
    event.target.checked = this.isCustomerActive;

    // Determine what action user wants to take
    const targetStatus = this.isCustomerActive ? 'inactive' : 'active';
    this.pendingStatus = targetStatus;

    // Show appropriate modal
    if (targetStatus === 'active') {
      this.showActiveModal = true;
    } else {
      this.showInactiveModal = true;
    }
  }

  onActiveModalClose(): void {
    this.showActiveModal = false;
    this.pendingStatus = '';
  }

  onInactiveModalClose(): void {
    this.showInactiveModal = false;
    this.pendingStatus = '';
  }

  onActiveModalConfirm(): void {
    this.updateCustomerStatus('active');
  }

  onInactiveModalConfirm(): void {
    this.updateCustomerStatus('inactive');
  }

  /**
   * Update the customer's status on the server, update local state, show feedback, and emit a statusUpdated event.
   * @example
   * updateCustomerStatus('active')
   * void
   * @param {{string}} {{status}} - New status to set for the customer (e.g., 'active' or 'inactive').
   * @returns {{void}} No return value; performs side effects like updating state, showing toasts, and emitting events.
   **/
  private updateCustomerStatus(status: string): void {
    if (!this.customer) return;

    this.isUpdatingStatus = true;

    this.customerService.updateCustomerStatus(this.customer.id, status).subscribe({
      next: response => {
        if (response.status) {
          // Update local customer data
          this.customer!.customer_status = status;
          this.isCustomerActive = status === 'active';

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Customer records updated!',
            detail: `${this.customer?.name || this.customer?.first_name + ' ' + this.customer?.last_name} as been updated successfully`,
          });

          // Emit status updated event
          this.statusUpdated.emit();
        } else {
          this.showErrorToast('Failed to update customer status.');
        }

        // Close modals and reset state
        this.showActiveModal = false;
        this.showInactiveModal = false;
        this.isUpdatingStatus = false;
        this.pendingStatus = '';
      },
      error: error => {
        console.error('Error updating customer status:', error);
        this.showErrorToast('An error occurred while updating customer status.');

        // Revert toggle state
        this.isCustomerActive = this.customer!.customer_status === 'active';

        // Close modals and reset state
        this.showActiveModal = false;
        this.showInactiveModal = false;
        this.isUpdatingStatus = false;
        this.pendingStatus = '';
      },
    });
  }

  private showErrorToast(message: string): void {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: message,
    });
  }

  onClose() {
    this.close.emit();
  }

  onBackdropClick(event: Event) {
    // Close modal when clicking on backdrop
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  getInitials(name: string): string {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  onCustomerUpdated(): void {
    // Emit customer updated event to parent component (table)
    this.customerUpdated.emit();
  }
}
